import { action, computed, autorun, observable } from 'mobx';

import { injectFacility } from 'app/facility';
import { isValidEmail } from 'app/utils/validators';

import TicketsBaseFormStore from 'modules/ticketPublicPage/stores/common/form';
import view from 'modules/ticketPublicPage/stores/tickets/view';

export class TicketsFormStore extends TicketsBaseFormStore {
  constructor(view) {
    super();
    this.view = view;
    autorun(() => {
      // We are automatically going to the next screen when a user returns from Paypal with a successful payment
      // https://github.com/resmio/support-requests/issues/809
      if (this.payPalPaymentReady) {
        this.onNextBtnClick();
      }
    });
  }
  facility = injectFacility();
  DETAILS = 'Details';
  PAYMENT = 'Payment';

  @observable accessor validPhone = false;
  @observable accessor stashedPhoneNumber = '';

  @computed get showPaymentDetailsFooter() {
    return this.step === this.INITIAL;
  }

  @action setCustomFormDefaults = () => {
    this.formValues.set('num', this.view.minNumber);
    this.formValues.set('name', '');
    this.formValues.set('surname', '');
    this.formValues.set('email', '');
    this.formValues.set('owner_name', '');
    this.formValues.set('owner_email', '');
  };

  @action validate() {
    if (this.errors.token) delete this.errors.token;
    let validateFields = [];
    switch (this.step) {
      case this.INITIAL:
        validateFields = ['num'];
        break;
      case this.DETAILS:
        validateFields = ['name', 'surname', 'email', 'phone'];
        break;
    }
    validateFields.forEach((field) => this.validateField(field));
  }

  @action validateField = (field) => {
    const value = this.formValues.get(field);
    switch (field) {
      case 'email':
        if (!value) {
          this.setError(field, i18n.gettext('Please add an email address.'));
        } else if (!isValidEmail(value))
          this.setError(
            field,
            i18n.gettext('Please input a valid email address.')
          );
        break;
      case 'phone':
        if (this.view.activeObject.type !== 'event') {
          break;
        }

        if (!value) {
          this.setError(field, i18n.gettext('Please add a phone number.'));
        } else if (!this.validPhone) {
          this.setError(
            field,
            i18n.gettext('Please input a valid phone number.')
          );
        } else {
          this.errors.delete('phone');
        }
        break;
      case 'name':
        if (!value) {
          this.setError(field, i18n.gettext('Please add your first name.'));
        }
        break;
      case 'surname':
        if (!value) {
          this.setError(field, i18n.gettext('Please add your last name.'));
        }
        break;
      default:
        if (!value)
          this.setError(field, i18n.gettext('Please fill in the field.'));
        break;
    }
  };

  @action setValidPhone = (value) => {
    this.validPhone = value;
  };

  @action stashPhoneNumber = () => {
    this.stashedPhoneNumber = this.formValues.get('phone');
  };

  @action deleteStashedPhoneNumber = () => {
    this.stashedPhoneNumber = '';
  };

  convertFormToServer = () => {
    const converted = { ticket_id: this.view.activeObject.id };
    Array.from(this.formValues.keys()).map((key) => {
      switch (key) {
        case 'num':
          converted.units = this.formValues.get(key);
          break;
        default:
          converted[key] = this.formValues.get(key);
          break;
      }
    });
    return converted;
  };

  @action onNextBtnClick = async () => {
    this.validate();
    if (this.hasErrors) return;
    let price;
    switch (this.step) {
      case this.INITIAL:
        this.step = this.DETAILS;
        break;
      case this.DETAILS:
        price = Number(this.view.activeObject.price);
        if (!price) {
          this.saveForm();
          break;
        }
        this.stashPhoneNumber();
        this.step = this.PAYMENT;
        break;
      case this.PAYMENT:
        price = Number(this.view.activeObject.price);
        if (!price) {
          this.saveForm();
          break;
        }
        if (this.formValues.get('payment_type') === this.paymentTypes.bambora) {
          await this.handleBamboraCheckout(price * this.formValues.get('num'));
        }
        this.validatePayment();
        break;
      case this.CONFIRMATION:
        this.step = this.INITIAL;
        view.setAvailable(view.available - this.formValues.get('num'));
        this.deleteStashedPhoneNumber();
        this.setFormDefaults();
        break;
    }
  };

  @action onBackBtnClick = () => {
    switch (this.step) {
      case this.INITIAL:
        if (this.facility.settings.website) {
          window.open(this.facility.settings.website);
        }
        break;
      case this.DETAILS:
        this.step = this.INITIAL;
        break;
      case this.PAYMENT:
        this.step = this.DETAILS;
        break;
    }
  };
}

const form = new TicketsFormStore(view);
export default form;
